import { createApi } from "@reduxjs/toolkit/query/react";
import { defaultApiConfig } from "./apiSettings";
import { defaultQueryStringify } from "libs";
import { WithPagination } from "./common.types";
import {
  BotActionLogInterface,
  BotBillingComposite,
  BotBillingEventInterface,
  BotInterface,
  BotInvoicesPage,
  BotPaymentMethod,
  BotPricingQuote,
  BotStatsForBot,
  BotStatsOverview,
  BotTaskInterface,
  CreateBotPayload,
  CreateBotTaskPayload,
  UpdateBillingDetailsPayload,
  UpdateBotPayload,
  UpdateBotTaskPayload,
} from "./bots.types";
import { $Object } from "../types/object";

export const botsApi = createApi({
  ...defaultApiConfig,
  reducerPath: "botsApi",
  tagTypes: [
    "Bots",
    "Bot",
    "BotTasks",
    "BotBilling",
    "BotInvoices",
    "BotStatsOverview",
    "BotStats",
    "BotActionLog",
    "BotBillingEvents",
  ],
  endpoints: (builder) => ({
    // ---- Bot CRUD ----

    getBots: builder.query<WithPagination<BotInterface>, $Object>({
      query: (params) => `admin/bots?${defaultQueryStringify(params)}`,
      providesTags: ["Bots"],
    }),
    getBot: builder.query<BotInterface, { id: number }>({
      query: ({ id }) => `admin/bots/${id}`,
      transformResponse: (res: { data: BotInterface }) => res.data,
      providesTags: (_, __, arg) => [{ type: "Bot", id: arg.id }],
    }),
    createBot: builder.mutation<BotInterface, CreateBotPayload>({
      query: (body) => ({ url: `admin/bots`, method: "POST", body }),
      transformResponse: (res: { data: BotInterface }) => res.data,
      invalidatesTags: ["Bots", "BotBilling", "BotStatsOverview"],
    }),
    updateBot: builder.mutation<BotInterface, UpdateBotPayload>({
      query: ({ id, ...body }) => ({
        url: `admin/bots/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res: { data: BotInterface }) => res.data,
      invalidatesTags: (_, __, arg) => ["Bots", { type: "Bot", id: arg.id }],
    }),
    archiveBot: builder.mutation<{ data: { archived: true } }, { id: number }>({
      query: ({ id }) => ({ url: `admin/bots/${id}`, method: "DELETE" }),
      invalidatesTags: ["Bots", "BotBilling", "BotStatsOverview"],
    }),
    pauseBot: builder.mutation<{ data: { status: number } }, { id: number }>({
      query: ({ id }) => ({ url: `admin/bots/${id}/pause`, method: "POST" }),
      invalidatesTags: (_, __, arg) => ["Bots", { type: "Bot", id: arg.id }],
    }),
    resumeBot: builder.mutation<{ data: { status: number } }, { id: number }>({
      query: ({ id }) => ({ url: `admin/bots/${id}/resume`, method: "POST" }),
      invalidatesTags: (_, __, arg) => ["Bots", { type: "Bot", id: arg.id }],
    }),

    // ---- Bot Tasks ----

    getBotTasks: builder.query<{ data: BotTaskInterface[] }, { botId: number }>({
      query: ({ botId }) => `admin/bots/${botId}/tasks`,
      providesTags: (_, __, arg) => [{ type: "BotTasks", id: arg.botId }],
    }),
    createBotTask: builder.mutation<BotTaskInterface, CreateBotTaskPayload>({
      query: ({ bot_id, ...body }) => ({
        url: `admin/bots/${bot_id}/tasks`,
        method: "POST",
        body,
      }),
      transformResponse: (res: { data: BotTaskInterface }) => res.data,
      invalidatesTags: (_, __, arg) => [{ type: "BotTasks", id: arg.bot_id }],
    }),
    updateBotTask: builder.mutation<BotTaskInterface, UpdateBotTaskPayload>({
      query: ({ id, bot_id: _ignored, ...body }) => ({
        url: `admin/bots/tasks/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res: { data: BotTaskInterface }) => res.data,
      invalidatesTags: (_, __, arg) =>
        arg.bot_id ? [{ type: "BotTasks", id: arg.bot_id }] : ["Bots"],
    }),
    deleteBotTask: builder.mutation<
      { data: { deleted: true } },
      { id: number; botId: number }
    >({
      query: ({ id }) => ({ url: `admin/bots/tasks/${id}`, method: "DELETE" }),
      invalidatesTags: (_, __, arg) => [{ type: "BotTasks", id: arg.botId }],
    }),
    toggleBotTask: builder.mutation<
      BotTaskInterface,
      { id: number; botId: number }
    >({
      query: ({ id }) => ({
        url: `admin/bots/tasks/${id}/toggle`,
        method: "POST",
      }),
      transformResponse: (res: { data: BotTaskInterface }) => res.data,
      invalidatesTags: (_, __, arg) => [{ type: "BotTasks", id: arg.botId }],
    }),

    // ---- Billing ----

    getBotBilling: builder.query<{ data: BotBillingComposite }, undefined>({
      query: () => "admin/bots/billing",
      providesTags: ["BotBilling"],
    }),
    updateBillingDetails: builder.mutation<
      { data: BotBillingComposite["billing_details"] },
      UpdateBillingDetailsPayload
    >({
      query: (body) => ({
        url: "admin/bots/billing/details",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["BotBilling"],
    }),
    createSetupIntent: builder.mutation<
      { data: { client_secret: string; id: string } },
      undefined
    >({
      query: () => ({
        url: "admin/bots/billing/setup-intent",
        method: "POST",
      }),
    }),
    attachPaymentMethod: builder.mutation<
      { data: { attached: true } },
      { payment_method_id: string }
    >({
      query: (body) => ({
        url: "admin/bots/billing/payment-method",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BotBilling"],
    }),
    listPaymentMethods: builder.query<{ data: BotPaymentMethod[] }, undefined>({
      query: () => "admin/bots/billing/payment-methods",
      providesTags: ["BotBilling"],
    }),
    listInvoices: builder.query<
      BotInvoicesPage,
      { limit?: number; starting_after?: string }
    >({
      query: (params) =>
        `admin/bots/billing/invoices?${defaultQueryStringify(params || {})}`,
      providesTags: ["BotInvoices"],
    }),
    pricingQuote: builder.query<
      { data: BotPricingQuote },
      { quantity?: number }
    >({
      query: ({ quantity }) =>
        quantity !== undefined
          ? `admin/bots/billing/quote?quantity=${quantity}`
          : "admin/bots/billing/quote",
    }),
    cancelSubscription: builder.mutation<
      { data: { scheduled_to_cancel: true } },
      undefined
    >({
      query: () => ({ url: "admin/bots/billing/cancel", method: "POST" }),
      invalidatesTags: ["BotBilling"],
    }),
    listGlobalBillingEvents: builder.query<
      WithPagination<BotBillingEventInterface>,
      $Object
    >({
      query: (params) =>
        `admin/bots/billing/events?${defaultQueryStringify(params)}`,
    }),

    // ---- Stats / Analytics ----

    getStatsOverview: builder.query<{ data: BotStatsOverview }, undefined>({
      query: () => "admin/bots/stats/overview",
      providesTags: ["BotStatsOverview"],
    }),
    getBotStats: builder.query<{ data: BotStatsForBot }, { botId: number }>({
      query: ({ botId }) => `admin/bots/${botId}/stats`,
      providesTags: (_, __, arg) => [{ type: "BotStats", id: arg.botId }],
    }),
    getBotActionLog: builder.query<
      WithPagination<BotActionLogInterface>,
      { botId: number } & $Object
    >({
      query: ({ botId, ...params }) =>
        `admin/bots/${botId}/action-log?${defaultQueryStringify(params)}`,
      providesTags: (_, __, arg) => [{ type: "BotActionLog", id: arg.botId }],
    }),
    getBotBillingEvents: builder.query<
      WithPagination<BotBillingEventInterface>,
      { botId: number } & $Object
    >({
      query: ({ botId, ...params }) =>
        `admin/bots/${botId}/billing-events?${defaultQueryStringify(params)}`,
      providesTags: (_, __, arg) => [{ type: "BotBillingEvents", id: arg.botId }],
    }),
  }),
});

export const {
  useGetBotsQuery,
  useGetBotQuery,
  useCreateBotMutation,
  useUpdateBotMutation,
  useArchiveBotMutation,
  usePauseBotMutation,
  useResumeBotMutation,

  useGetBotTasksQuery,
  useCreateBotTaskMutation,
  useUpdateBotTaskMutation,
  useDeleteBotTaskMutation,
  useToggleBotTaskMutation,

  useGetBotBillingQuery,
  useUpdateBillingDetailsMutation,
  useCreateSetupIntentMutation,
  useAttachPaymentMethodMutation,
  useListPaymentMethodsQuery,
  useListInvoicesQuery,
  usePricingQuoteQuery,
  useLazyPricingQuoteQuery,
  useCancelSubscriptionMutation,
  useListGlobalBillingEventsQuery,

  useGetStatsOverviewQuery,
  useGetBotStatsQuery,
  useGetBotActionLogQuery,
  useGetBotBillingEventsQuery,
} = botsApi;
